import {
  Dropdown,
  IconButton,
  SvgArchive,
  SvgCopy,
  SvgDomain,
  SvgDownload,
  SvgEdit,
  SvgFolder,
  SvgLock,
  SvgMoreHoriz,
  SvgStar,
  SvgStarOutline,
  SvgTrash,
  SvgUnarchive,
  SvgUsersAdd,
} from '@loomhq/lens';
import copy from 'copy-to-clipboard';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { openModal } from 'actions/modals';
import { updateShowMoveModal } from 'actions/moveModal';
import { showCollaborateModal } from 'actions/videosDashboard';
import {
  CLICKED_SHARE_WITH_TEAM,
  CLICKED_UNDO_SHARE_WITH_TEAM,
  DUPLICATE_ITEM_CLICK,
  ORGANIZE_VIDEO,
} from 'constants/events';
import * as menuOptions from 'constants/menuOptions';
import { PRIVACY_CHANGE_MOVE_CONFIRMATION_MODAL } from 'constants/modalTypes';
import { LOOM_URI } from 'constants/routes';
import {
  PERSONAL_ARCHIVE_WRITE,
  SHARED_LIBRARY_WRITE,
  TEAM_ARCHIVE_WRITE,
  VIDEO_TOOLS_ACCESS,
} from 'constants/scopes';
import { VIDEO } from 'constants/videosDashboard';
import { startDownload } from 'creators/header';
import {
  archiveVideoCreator,
  deleteVideoCreator,
  moveVideoCreator,
  toggleVideoFavoriteCreator,
} from 'creators/shareVideo';
import { duplicateVideoCreator } from 'creators/video';
import * as analytics from 'utilities/analytics';

import { hasScope } from 'selectors/scopes';

import {
  MY_VIDEOS_FOLDER_NAME,
  SPECIAL_FOLDER_ID_MY_VIDEOS,
  SPECIAL_FOLDER_ID_TEAM_VIDEOS,
  TEAM_VIDEOS_FOLDER_NAME,
} from '../../../../../shared/constants/folders';
import { VIDEO_PRIVACY_OWNER } from '../../../../../shared/constants/video';
import {
  PRIVATE_WORKSPACE,
  TEAM_WORKSPACE,
} from '../../../../../shared/constants/workspaces';

import HideDuringDowntime from 'components/hide-during-downtime';

const getOptions = ({
  canArchive,
  canDelete,
  canInviteToEdit,
  canStar,
  dispatch,
  downloadUrl,
  hasToolsAccess,
  triggerFocusTitle,
  video,
  videoIsTeamShared,
}) => {
  const selectedItem = { id: video.id, name: video.name, type: 'video' };
  const renameVideo = () => triggerFocusTitle();
  const moveVideo = () => {
    dispatch(updateShowMoveModal(true, [selectedItem]));

    analytics.track(ORGANIZE_VIDEO, {
      root_folder: video.folder_id,
      is_owner: video.current_user_is_owner,
    });
  };
  const inviteToEdit = () => {
    dispatch(
      showCollaborateModal(video.id, video.name, false, VIDEO, video.owner_id)
    );
  };

  const archiveVideo = async () => {
    await dispatch(archiveVideoCreator(video.id));
  };

  const deleteVideo = () => {
    if (!confirm('Are you sure you want to permanently delete this video?')) {
      return;
    }

    dispatch(deleteVideoCreator(video.id));
  };

  const duplicateVideo = () => {
    const newName = `Copy of ${video.name}`;

    dispatch(duplicateVideoCreator(video.id, newName))
      .then(newVideo => {
        const url = `${LOOM_URI}/share/${newVideo.id}`;

        copy(url, { format: 'text/plain' });
        setTimeout(() => {
          window.open(url, '_blank');
        }, 1000);
      })
      .then(() => {
        analytics.track(DUPLICATE_ITEM_CLICK, {
          entity_id: video.id,
          entity_type: 'video',
          click_source: 'share-video',
        });
      });
  };

  const toggleFavoriteStatus = () => {
    dispatch(toggleVideoFavoriteCreator(video));

    // we don't store the video record in a redux store,
    // so we just update the record in memory
    // hacky but works because the menu is closed when any option is chosen
    // so when we next render the menu, this will appear correctly
    video.isUserFavorite = !video.isUserFavorite;
  };

  const moveVideoToTeam = () => {
    analytics.track(CLICKED_SHARE_WITH_TEAM);
    dispatch(
      moveVideoCreator(
        SPECIAL_FOLDER_ID_TEAM_VIDEOS,
        TEAM_VIDEOS_FOLDER_NAME,
        false,
        TEAM_WORKSPACE
      )
    );
  };

  const moveVideoToPersonal = () => {
    analytics.track(CLICKED_UNDO_SHARE_WITH_TEAM);
    dispatch(
      moveVideoCreator(
        SPECIAL_FOLDER_ID_MY_VIDEOS,
        MY_VIDEOS_FOLDER_NAME,
        false,
        PRIVATE_WORKSPACE
      )
    );
  };

  const options = [];

  if (canArchive) {
    options.push({
      title: menuOptions.ARCHIVE,
      onClick: archiveVideo,
      icon: video.archived ? SvgUnarchive : SvgArchive,
    });
  }

  if (canStar) {
    options.push({
      title: video.isUserFavorite ? menuOptions.UNSTAR : menuOptions.STAR,
      onClick: toggleFavoriteStatus,
      icon: video.isUserFavorite ? SvgStar : SvgStarOutline,
    });
  }

  if (hasToolsAccess) {
    options.push({
      title: menuOptions.DUPLICATE,
      onClick: duplicateVideo,
      icon: SvgCopy,
    });
  }

  if (canInviteToEdit) {
    options.push({
      title: menuOptions.INVITE_TO_EDIT,
      onClick: inviteToEdit,
      icon: SvgUsersAdd,
    });
  }

  if (hasToolsAccess) {
    options.push(
      {
        title: menuOptions.MOVE,
        onClick: moveVideo,
        icon: SvgFolder,
      },
      {
        title: menuOptions.RENAME,
        onClick: renameVideo,
        icon: SvgEdit,
      }
    );

    if (videoIsTeamShared) {
      options.push({
        title: menuOptions.MOVE_PERSONAL_LIBRARY,
        onClick: moveVideoToPersonal,
        icon: SvgLock,
      });
    } else {
      options.push({
        title: menuOptions.SHARE_WITH_TEAM,
        onClick: () => {
          if (video.privacy === VIDEO_PRIVACY_OWNER) {
            dispatch(
              openModal(PRIVACY_CHANGE_MOVE_CONFIRMATION_MODAL, {
                onConfirm: moveVideoToTeam,
              })
            );
          } else {
            moveVideoToTeam();
          }
        },
        icon: SvgDomain,
      });
    }
  }

  if (canDelete) {
    options.push({
      title: menuOptions.DELETE,
      onClick: deleteVideo,
      icon: SvgTrash,
    });
  }

  options.push({
    title: menuOptions.DOWNLOAD,
    disabled: !downloadUrl,
    icon: SvgDownload,
  });

  const optionSort = (a, b) => {
    return a.title < b.title ? -1 : a.title > b.title ? 1 : 0;
  };

  return (
    options
      // we call the icon function since it's a component
      .map(({ icon, ...otherOptions }) => ({ icon: icon(), ...otherOptions }))
      .sort(optionSort)
  );
};

export default ({ downloadUrl, triggerFocusTitle, user, video }) => {
  const dispatch = useDispatch();

  const currentUserIsOwner = video.current_user_is_owner;
  const videoIsSharedWithUser =
    Object.keys(video.collaboratePermissions).length > 0;

  const scopes = useSelector(state => ({
    hasPersonalArchiveWriteScope: hasScope(state, PERSONAL_ARCHIVE_WRITE),
    hasSharedLibraryWriteScope: hasScope(state, SHARED_LIBRARY_WRITE),
    hasTeamArchiveWriteScope: hasScope(state, TEAM_ARCHIVE_WRITE),
    hasVideoToolsAccessScope: hasScope(state, VIDEO_TOOLS_ACCESS),
  }));

  const videoIsTeamShared = useSelector(
    state => state?.share_video?.video_is_team_shared
  );

  const hasToolsAccess =
    (currentUserIsOwner || videoIsTeamShared) &&
    scopes.hasVideoToolsAccessScope;

  const canInviteToEdit =
    (currentUserIsOwner || videoIsTeamShared) &&
    (scopes.hasVideoToolsAccessScope || scopes.hasSharedLibraryWriteScope);

  const isLoggedIn = Boolean(user?.id);

  const canStar = isLoggedIn;

  const canDelete =
    currentUserIsOwner ||
    videoIsTeamShared ||
    video.collaboratePermissions.delete;

  const canArchive =
    canDelete &&
    (videoIsTeamShared
      ? scopes.hasTeamArchiveWriteScope
      : !videoIsSharedWithUser && scopes.hasPersonalArchiveWriteScope);

  const downloadName = `${video.name || video.id}.mp4`;

  const options = getOptions({
    canArchive,
    canDelete,
    canInviteToEdit,
    canStar,
    dispatch,
    downloadUrl,
    hasToolsAccess,
    triggerFocusTitle,
    video,
    videoIsTeamShared,
  });

  if (!options?.length) {
    return null;
  }

  const downloadVideo = () => {
    dispatch(
      startDownload({
        url: downloadUrl,
        name: downloadName,
        opts: {
          video_id: video.id,
          is_owner: video.current_user_is_owner,
        },
      })
    );
  };

  options.find(
    opt => opt.title === menuOptions.DOWNLOAD
  ).onClick = downloadVideo;

  if (options.length === 1) {
    const { title, icon, onClick, disabled } = options[0];

    return (
      <IconButton
        altText={title}
        icon={icon}
        onClick={onClick}
        isDisabled={disabled}
      />
    );
  }

  return (
    <HideDuringDowntime>
      <Dropdown
        options={options}
        trigger={
          <IconButton altText="Toggle actions" icon={<SvgMoreHoriz />} />
        }
        menuPosition="right"
        menuZIndex={isLoggedIn ? 100 : 1001}
      />
    </HideDuringDowntime>
  );
};
